import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  Renderer2,
  TemplateRef,
} from '@angular/core';
import { ConnectedOverlayScrollHandler } from 'primeng/dom';

@Directive({ selector: '[appTooltipNew]', standalone: true })
export class TooltipDirectiveNew implements AfterViewInit, OnDestroy {
  appendTo = input<HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined>('body');
  appTooltipNew = input<string | TemplateRef<HTMLElement> | undefined>();
  autoHide = input<boolean | undefined>(true);
  delayMs = input<number | undefined>(0);
  placement = input<'top' | 'bottom' | 'left' | 'right' | undefined>('right');
  /**
   * Describes when the tooltip will be shown.
   * - `immediate`: The tooltip will be shown after the delay timer expires.
   * - `shy`: The tooltip will be shown after holding the cursor still until the timer expires, timer will be reset on mouse move.
   *
   * If delay is set to 0, the tooltip will be shown immediately.
   *
   * @default 'immediate'
   */
  showMode = input<'immediate' | 'shy' | undefined>('immediate');
  showOn = input<'hover' | 'focus' | 'both' | undefined>('hover');
  theme = input<'light' | 'dark'>('dark');
  zIndex = input<number | 'auto' | undefined>('auto');

  el: ElementRef = inject(ElementRef);
  renderer: Renderer2 = inject(Renderer2);

  active: boolean | undefined;
  container: any;
  toolTipText: any;

  private _mouseEnterListener: Function | null = null;
  private _mouseLeaveListener: Function | null = null;
  private _mouseMoveListener: Function | null = null;
  private _clickListener: Function | null = null;
  private _focusListener: Function | null = null;
  private _blurListener: Function | null = null;
  private _containerMouseleaveListener: Function | null = null;
  private _scrollHandler: ConnectedOverlayScrollHandler | null = null;
  private _activateTimer: any | null = null;
  private _resizeListener: any | null = null;

  constructor(public zone: NgZone) {}

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      const showOn = this.showOn();
      this._mouseMoveListener = this._onMouseMove.bind(this);
      this._blurListener = this._onBlur.bind(this);
      if (showOn === 'hover' || showOn === 'both') {
        this._mouseEnterListener = this._onMouseEnter.bind(this);
        this._mouseLeaveListener = this._onMouseLeave.bind(this);

        this._clickListener = this.onInputClick.bind(this);

        this.el.nativeElement.addEventListener('mouseenter', this._mouseEnterListener);
        this.el.nativeElement.addEventListener('mouseleave', this._mouseLeaveListener);
        this.el.nativeElement.addEventListener('click', this._clickListener);
      }

      if (showOn === 'focus' || showOn === 'both') {
        this._focusListener = this._onFocus.bind(this);

        this.el.nativeElement.addEventListener('focus', this._focusListener);
      }
      this.el.nativeElement.addEventListener('blur', this._blurListener);
      this.el.nativeElement.addEventListener('mousemove', this._mouseMoveListener);
    });
  }

  ngOnChanges(simpleChanges: any) {
    if (simpleChanges['appTooltipNew']) {
      this._updateText();
    }
  }

  ngOnDestroy() {
    this._unbindEvents();

    this._remove();

    if (this._scrollHandler) {
      this._scrollHandler.destroy();
      this._scrollHandler = null;
    }
  }

  activate(immediate?: boolean) {
    if (this.active) {
      return;
    }
    if (this._activateTimer !== undefined) {
      clearTimeout(this._activateTimer);
      this._activateTimer = undefined;
    }
    if (immediate) {
      this.active = true;
      this.show();
    } else {
      this._activateTimer = setTimeout(() => {
        this.active = true;
        this.show();
      }, this.delayMs());
    }
  }

  align() {
    const placement = this.placement();

    if (!placement) {
      return;
    }

    const positionPriority = {
      top: [this._alignTop, this._alignBottom, this._alignRight, this._alignLeft],
      bottom: [this._alignBottom, this._alignTop, this._alignRight, this._alignLeft],
      left: [this._alignLeft, this._alignRight, this._alignTop, this._alignBottom],
      right: [this._alignRight, this._alignLeft, this._alignTop, this._alignBottom],
    };

    for (let [index, alignmentFn] of positionPriority[placement].entries()) {
      if (index === 0) alignmentFn.call(this);
      else if (this._isOutOfBounds()) alignmentFn.call(this);
      else break;
    }
  }

  appendChild(element: any, target: any) {
    if (this._isElement(target)) target.appendChild(element);
    else if (target && target.el && target.el.nativeElement) target.el.nativeElement.appendChild(element);
    else throw 'Cannot append ' + target + ' to ' + element;
  }

  create() {
    if (this.container) {
      this._remove();
    }

    this.container = document.createElement('div');
    this.container.setAttribute('id', 'tooltip-container');
    this.container.setAttribute('role', 'tooltip');

    this.toolTipText = document.createElement('div');
    this.toolTipText.className = 'new-tooltip-text';

    this._updateText();

    this.container.appendChild(this.toolTipText);

    const appendTo = this.appendTo();

    if (appendTo === 'body') {
      document.body.appendChild(this.container);
    } else if (appendTo === 'target') {
      this.el.nativeElement.appendChild(this.container);
    } else {
      this.appendChild(this.container, this.el.nativeElement);
    }

    this.container.style.display = 'none';

    const isAutoHide = this.autoHide();

    if (isAutoHide) {
      this.container.style.pointerEvents = 'none';
    }
  }

  deactivate() {
    this.active = false;
    this.container.style.zIndex = '';

    this._remove();
  }

  hide() {
    this.container.style.zIndex = '';

    this._remove();
  }

  show() {
    this.create();

    this.container && (this.container.style.display = 'inline-block');
    this.align();

    this.container.style.zIndex = 1100;

    this._bindDocumentResizeListener();
    this._bindScrollListener();
  }

  onInputClick(e: Event) {
    this.activate(true);
  }

  private _alignBottom() {
    this._preAlign('bottom');
    let offsetLeft = (this._getOuterWidth(this.el.nativeElement) - this._getOuterWidth(this.container)) / 2;
    let offsetTop = this._getOuterHeight(this.el.nativeElement);
    this._alignTooltip(offsetLeft, offsetTop);
  }

  private _alignLeft() {
    this._preAlign('left');
    let offsetLeft = this._getOuterWidth(this.container);
    let offsetTop = (this._getOuterHeight(this.el.nativeElement) - this._getOuterHeight(this.container)) / 2;
    this._alignTooltip(-offsetLeft, offsetTop);
  }

  private _alignRight() {
    this._preAlign('right');
    const el = this.el.nativeElement;
    const offsetLeft = this._getOuterWidth(el);
    const offsetTop = (this._getOuterHeight(el) - this._getOuterHeight(this.container)) / 2;
    this._alignTooltip(offsetLeft, offsetTop);
  }

  private _alignTooltip(offsetLeft: number, offsetTop: number) {
    let hostOffset = this._getHostOffset();
    let left = hostOffset.left + offsetLeft;
    let top = hostOffset.top + offsetTop;
    this.container.style.left = left + 'px';
    this.container.style.top = top + 'px';
  }

  private _alignTop() {
    this._preAlign('top');
    let offsetLeft = (this._getOuterWidth(this.el.nativeElement) - this._getOuterWidth(this.container)) / 2;
    let offsetTop = this._getOuterHeight(this.container);
    this._alignTooltip(offsetLeft, -offsetTop);
  }

  private _bindContainerMouseleaveListener() {
    if (!this._containerMouseleaveListener) {
      const targetEl: any = this.container ?? this.container.nativeElement;

      this._containerMouseleaveListener = this.renderer.listen(targetEl, 'mouseleave', (e) => {
        this.deactivate();
      });
    }
  }

  private _bindDocumentResizeListener() {
    this.zone.runOutsideAngular(() => {
      this._resizeListener = this._onWindowResize.bind(this);
      window.addEventListener('resize', this._resizeListener);
    });
  }

  private _bindScrollListener() {
    if (!this._scrollHandler) {
      this._scrollHandler = new ConnectedOverlayScrollHandler(this.el.nativeElement, () => {
        if (this.container) {
          this.hide();
        }
      });
    }

    this._scrollHandler.bindScrollListener();
  }

  private _getHostOffset() {
    const appendTo = this.appendTo();
    if (appendTo === 'body') {
      let offset = this.el.nativeElement.getBoundingClientRect();
      let targetLeft = offset.left + this._getWindowScrollLeft();
      let targetTop = offset.top + this._getWindowScrollTop();

      return { left: targetLeft, top: targetTop };
    } else {
      return { left: 0, top: 0 };
    }
  }

  private _getOuterHeight(el: HTMLElement, margin?: boolean): number {
    let height = el.offsetHeight;

    if (margin) {
      let style = getComputedStyle(el);
      height += parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    }

    return height;
  }

  private _getOuterWidth(el: HTMLElement, margin?: boolean): number {
    let width = el.offsetWidth;

    if (margin) {
      let style = getComputedStyle(el);
      width += parseFloat(style.marginLeft) + parseFloat(style.marginRight);
    }

    return width;
  }

  private _getViewport(): any {
    let win = window,
      d = document,
      e = d.documentElement,
      g = d.getElementsByTagName('body')[0],
      w = win.innerWidth || e.clientWidth || g.clientWidth,
      h = win.innerHeight || e.clientHeight || g.clientHeight;

    return { width: w, height: h };
  }

  private _getWindowScrollLeft(): number {
    let doc = document.documentElement;
    const scrollLeft = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    return scrollLeft;
  }

  private _getWindowScrollTop(): number {
    let doc = document.documentElement;
    const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    return scrollTop;
  }

  private _isElement(obj: any) {
    return typeof HTMLElement === 'object'
      ? obj instanceof HTMLElement
      : obj && typeof obj === 'object' && obj !== null && obj.nodeType === 1 && typeof obj.nodeName === 'string';
  }

  private _isOutOfBounds(): boolean {
    let offset = this.container.getBoundingClientRect();
    let targetTop = offset.top;
    let targetLeft = offset.left;
    let width = this._getOuterWidth(this.container);
    let height = this._getOuterHeight(this.container);
    let viewport = this._getViewport();

    return (
      targetLeft + width > viewport.width || targetLeft < 0 || targetTop < 0 || targetTop + height > viewport.height
    );
  }

  private _onBlur(e: Event) {
    this.deactivate();
  }

  private _onFocus(e: Event) {
    this.activate();
  }

  private _onMouseEnter(e: Event) {
    if (!this.container) {
      this.activate();
    }
  }

  private _onMouseLeave(e: Event) {
    if (this.container) {
      this.deactivate();
    }
  }

  private _onMouseMove(e: Event) {
    if (this.showMode() === 'shy') {
      this.activate();
    }
  }

  private _onWindowResize(e: Event) {
    this.hide();
  }

  private _preAlign(position: string) {
    this.container.style.left = -999 + 'px';
    this.container.style.top = -999 + 'px';

    let defaultClassName = `new-tooltip new-tooltip-${position} new-tooltip-${this.theme()}`;
    this.container.className = defaultClassName;
  }

  private _remove() {
    if (this.container && this.container.parentElement) {
      const appendTo = this.appendTo();

      if (appendTo === 'body') {
        document.body.removeChild(this.container);
      } else if (appendTo === 'target') {
        this.el.nativeElement.removeChild(this.container);
      }
    }

    this._unbindDocumentResizeListener();
    this._unbindScrollListener();
    this._unbindContainerMouseleaveListener();

    this.container = null;
    this._scrollHandler = null;
  }

  private _unbindContainerMouseleaveListener() {
    if (this._containerMouseleaveListener) {
      this._bindContainerMouseleaveListener();
      this._containerMouseleaveListener = null;
    }
  }

  private _unbindDocumentResizeListener() {
    if (this._resizeListener) {
      window.removeEventListener('resize', this._resizeListener);
      this._resizeListener = null;
    }
  }

  private _unbindEvents() {
    const showOn = this.showOn();

    if (showOn === 'hover' || showOn === 'both') {
      this.el.nativeElement.removeEventListener('mouseenter', this._mouseEnterListener);
      this.el.nativeElement.removeEventListener('mouseleave', this._mouseLeaveListener);
      this.el.nativeElement.removeEventListener('click', this._clickListener);
    }

    if (showOn === 'focus' || showOn === 'both') {
      this.el.nativeElement.removeEventListener('focus', this._focusListener);
      this.el.nativeElement.removeEventListener('blur', this._blurListener);
    }

    this._unbindDocumentResizeListener();
  }

  private _unbindScrollListener() {
    if (this._scrollHandler) {
      this._scrollHandler.unbindScrollListener();
    }
  }

  private _updateText() {
    const content = this.appTooltipNew();
    if (this.toolTipText === undefined) {
      return;
    }

    this.toolTipText.innerHTML = content;
    this.align();
  }
}
